import { takeEvery, fork, takeLatest } from 'redux-saga/effects';

// Action Types
import {
  AuthActions,
  ReportsActions,
  SRUsersActions,
  UsersActions,
  ProfilesActions,
  DictionaryActions,
  ChartsActions,
  WPBlogActions,
  WebhooksActions,
  SettingsActions,
} from '@/store/actions';

// Slices
import { getAppDictionaryAction, switchDefaultPartnerAction } from '@/store/slices/app.slice';

// Saga functions
import { getAppDictionarySaga, switchDefaultPartnerSaga } from './app.ts';
import {
  authCheckLocalStorageSaga,
  registerSaga,
  authSaga,
  authWithAuth0Saga,
  createPasswordSaga,
  resetPassSendEmailSaga,
  resetPassUpdateSaga,
  logoutSaga,
  authAsSaga,
} from './auth';
import {
  getReportsSaga,
  getReportDetailsSaga,
  downloadReportSaga,
  setReportUserFieldsSaga,
  getReportDictionarySaga,
  getScheduleSettingsSaga,
  saveScheduleSettingsSaga,
  extendInviteSaga,
} from './reports';
import {
  getSRUserDetailsSaga,
  getSRUserPaymentsSaga,
  sendSRUserTransferRequestSaga,
} from './srUsers';
import { getUsersSaga, addUserSaga, updateUserSaga, activateUserSaga } from './users';
import { getProfilesSaga, loginAsProfileUserSaga } from './profiles';
import { getDictionarySaga } from './dictionary';
import { getChartsSaga } from './charts';
import { getWPPageSaga } from './wpBlog';
import {
  addCustomerCreditsSaga,
  updateCustomerCreditsSaga,
  cancelCustomerCreditsSaga,
  getWebhooksDictionarySaga,
} from './webhooks';
import { getSettingsDictionarySaga, updateSettingsDictionarySaga } from './settings';

export default function* rootSaga() {
  yield fork(authCheckLocalStorageSaga);

  // COMMON
  yield takeLatest(getAppDictionaryAction.type, getAppDictionarySaga);
  yield takeLatest(switchDefaultPartnerAction.type, switchDefaultPartnerSaga);

  // Auth
  yield takeEvery(AuthActions.login.types.INIT, authSaga);
  yield takeEvery(AuthActions.loginAs.types.INIT, authAsSaga);
  yield takeEvery(AuthActions.loginWithAuth0.types.INIT, authWithAuth0Saga);
  yield takeEvery(AuthActions.registerUser.types.INIT, registerSaga);
  yield takeEvery(AuthActions.createPassword.types.INIT, createPasswordSaga);
  yield takeEvery(AuthActions.resetPasswordSendEmail.types.INIT, resetPassSendEmailSaga);
  yield takeEvery(AuthActions.resetPassUpdate.types.INIT, resetPassUpdateSaga);
  yield takeEvery(AuthActions.logout.types.INIT, logoutSaga);

  // Reports
  yield takeEvery(ReportsActions.getReports.types.INIT, getReportsSaga);
  yield takeEvery(ReportsActions.getReportDetails.types.INIT, getReportDetailsSaga);
  yield takeEvery(ReportsActions.generateReport.types.INIT, downloadReportSaga);
  yield takeLatest(ReportsActions.setReportUserFields.types.INIT, setReportUserFieldsSaga);
  yield takeLatest(ReportsActions.getReportDictionary.types.INIT, getReportDictionarySaga);
  yield takeEvery(ReportsActions.getScheduleSettings.types.INIT, getScheduleSettingsSaga);
  yield takeEvery(ReportsActions.saveScheduleSettings.types.INIT, saveScheduleSettingsSaga);
  yield takeLatest(ReportsActions.extendInvite.types.INIT, extendInviteSaga);

  // SR Users
  yield takeEvery(SRUsersActions.getSRUserDetails.types.INIT, getSRUserDetailsSaga);
  yield takeEvery(SRUsersActions.getSRUserPayments.types.INIT, getSRUserPaymentsSaga);
  yield takeEvery(
    SRUsersActions.sendSRUserTransferRequest.types.INIT,
    sendSRUserTransferRequestSaga,
  );

  // Users
  yield takeLatest(UsersActions.getUsers.types.INIT, getUsersSaga);
  yield takeEvery(UsersActions.addUser.types.INIT, addUserSaga);
  yield takeEvery(UsersActions.updateUser.types.INIT, updateUserSaga);
  yield takeEvery(UsersActions.activateUser.types.INIT, activateUserSaga);

  // MB Users
  yield takeEvery(ProfilesActions.getProfiles.types.INIT, getProfilesSaga);
  yield takeEvery(ProfilesActions.loginAsProfileUser.types.INIT, loginAsProfileUserSaga);

  // Dictionary
  yield takeEvery(DictionaryActions.getDictionary.types.INIT, getDictionarySaga);

  // Charts
  yield takeEvery(ChartsActions.getCharts.types.INIT, getChartsSaga);

  // WP BLOG
  yield takeLatest(WPBlogActions.getWPPage.types.INIT, getWPPageSaga);

  // WEBHOOKS
  yield takeEvery(WebhooksActions.getWebhooksDictionary.types.INIT, getWebhooksDictionarySaga);
  yield takeEvery(WebhooksActions.addCustomerCredits.types.INIT, addCustomerCreditsSaga);
  yield takeEvery(WebhooksActions.updateCustomerCredits.types.INIT, updateCustomerCreditsSaga);
  yield takeEvery(WebhooksActions.cancelCustomerCredits.types.INIT, cancelCustomerCreditsSaga);

  // SETTINGS
  yield takeEvery(SettingsActions.getSettingsDictionary.types.INIT, getSettingsDictionarySaga);
  yield takeEvery(
    SettingsActions.updateSettingsDictionary.types.INIT,
    updateSettingsDictionarySaga,
  );
}
